// src/components/serverSetup/ServerSetup.js

import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Backdrop,
  DialogContentText,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  SwapHoriz as SwapHorizIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import ServerSelector from './ServerSelector';
import ChannelConfiguration from './ChannelConfiguration';
import NFTConfiguration from './NFTConfiguration';
import TraitSelectionModal from './TraitSelectionModal';
import CreateRoleModal from './CreateRoleModal';
import PurchaseSlotModal from './PurchaseSlotModal';
import SolanaPaymentModal from '../SolanaPaymentModal';
import ConfirmConfigurationModal from './ConfirmConfigurationModal';
import EditCollectionModal from './EditCollectionModal';

import { AuthContext } from '../../context/AuthContext';

import Logo from '../../assets/Mindao_logo_extracted.png'; // Ensure correct path

// Custom Scrollbar styling
const CustomScrollbarBox = styled(Box)(({ theme }) => ({
  maxHeight: '450px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#AF3B72',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#C2CCDF',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: '#AF3B72 #050304',
}));

  // Add this function outside of your component or within the component before usage
  const parseSubscriptionExpiresAt = (expiresAt) => {
    if (typeof expiresAt === 'string') {
      return new Date(expiresAt);
    } else if (expiresAt instanceof Date) {
      return expiresAt;
    } else if (typeof expiresAt === 'object' && expiresAt !== null) {
      if ('seconds' in expiresAt && 'nanoseconds' in expiresAt) {
        return new Date(expiresAt.seconds * 1000 + expiresAt.nanoseconds / 1000000);
      } else if ('_seconds' in expiresAt && '_nanoseconds' in expiresAt) {
        return new Date(expiresAt._seconds * 1000 + expiresAt._nanoseconds / 1000000);
      }
    }
    console.warn('ServerSetup: Unknown format for subscriptionExpiresAt:', expiresAt);
    return new Date('Invalid Date');
  };


const ServerSetup = () => {
  const {
    guilds,
    selectedGuild,
    setSelectedGuild,
    accessToken,
    setGuildId,
    user,
  } = useContext(AuthContext); // Access context
  
  const navigate = useNavigate();

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Shared States
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [collectionList, setCollectionList] = useState([]); // List of collections
  const [traits, setTraits] = useState([]);
  const [isTraitModalOpen, setIsTraitModalOpen] = useState(false);
  const [selectedTraitIndex, setSelectedTraitIndex] = useState(null);

  // Additional States Defined
  const [channel, setChannel] = useState('');
  const [newChannelName, setNewChannelName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [role, setRole] = useState('');
  const [permissionList, setPermissionList] = useState([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleColor, setNewRoleColor] = useState('');
  const [newRoleHoist, setNewRoleHoist] = useState(false);
  const [newRoleMentionable, setNewRoleMentionable] = useState(false);
  const [creatingRole, setCreatingRole] = useState(false);
  const [newRolePermissions, setNewRolePermissions] = useState([]);
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);

  // States for Collection Management
  const [newCollectionAddress, setNewCollectionAddress] = useState('');
  const [isAddCollectionModalOpen, setIsAddCollectionModalOpen] = useState(false);

  // States for Role Configurations
  const [roleConfigurations, setRoleConfigurations] = useState([]); // Ensure this is defined

  // States for Changing Collection
  const [isChangeCollectionModalOpen, setIsChangeCollectionModalOpen] = useState(false);
  const [collectionToChange, setCollectionToChange] = useState(null); // Currently active collection

  // State for handling collection edits
  const [collectionToEdit, setCollectionToEdit] = useState(null);
  const [collectionToUpdate, setCollectionToUpdate] = useState(null);


  // States for Confirming Collection Removal
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false);
  const [collectionToRemove, setCollectionToRemove] = useState(null);

  // Loading state for Save Configuration
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  const [subscriptionLevel, setSubscriptionLevel] = useState('basic');
  const [additionalCollectionSlots, setAdditionalCollectionSlots] = useState(0);
  const [maxCollectionsAllowed, setMaxCollectionsAllowed] = useState(1);

  const [isPurchaseSlotModalOpen, setIsPurchaseSlotModalOpen] = useState(false);
  const [slotsToPurchase, setSlotsToPurchase] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isPaymentProcessed, setIsPaymentProcessed] = useState(false);

  const [isSolanaPaymentModalOpen, setIsSolanaPaymentModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0.5); // Adjust the amount as needed
  const [paymentReason, setPaymentReason] = useState('');

  const [collectionBeingUnlocked, setCollectionBeingUnlocked] = useState(null);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [modalConfigData, setModalConfigData] = useState({});

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentCollectionAddress, setCurrentCollectionAddress] = useState('');

  const [serverConfigs, setServerConfigs] = useState([]);

  useEffect(() => {
    const fetchAllServerConfigs = async () => {
      if (!guilds || guilds.length === 0) {
        setServerConfigs([]);
        return;
      }

      try {
        // Create an array of promises to fetch server-config for each guild
        const configPromises = guilds.map((guild) =>
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/server-config?guildId=${guild.id}`)
            .then(response => ({ guildId: guild.id, config: response.data }))
            .catch(error => {
              if (error.response && error.response.status === 404) {
                console.warn(`Server-config not found for guild ${guild.id}.`);
                return { guildId: guild.id, config: null };
              } else {
                console.error(`Error fetching server config for guild ${guild.id}:`, error);
                return { guildId: guild.id, config: null };
              }
            })
        );

        // Wait for all promises to resolve
        const results = await Promise.all(configPromises);

        // Log the fetched server configurations
        console.log('Fetched server configurations:', results);

        // Update the serverConfigs state
        setServerConfigs(results);
      } catch (error) {
        console.error('Error fetching server configurations for all guilds:', error);
        setServerConfigs([]); // Reset to empty array on error
      }
    };

    fetchAllServerConfigs();
  }, [guilds, accessToken]);

  // Compute active guilds based on serverConfigs and subscription data
const activeGuilds = useMemo(() => {
  return guilds.filter((guild) => {
    const serverConfig = serverConfigs.find((sc) => sc.guildId === guild.id)?.config;

    if (!serverConfig) {
      console.warn(`No serverConfig found for guild ${guild.id}.`);
      return false;
    }

    const { isSubscribed, subscriptionExpiresAt } = serverConfig;

    if (!isSubscribed) {
      console.log(`Guild ID: ${guild.id} is not subscribed.`);
      return false;
    }

    const subscriptionExpiryDate = parseSubscriptionExpiresAt(subscriptionExpiresAt);

    if (isNaN(subscriptionExpiryDate.getTime())) {
      console.warn(`Invalid subscriptionExpiresAt for guild ${guild.id}:`, subscriptionExpiresAt);
      return false;
    }

    const now = new Date();

    const isActive = subscriptionExpiryDate > now;

    // Log for debugging
    console.log(`Guild ID: ${guild.id}`);
    console.log(`Is Subscribed: ${isSubscribed}`);
    console.log(`Subscription Expires At: ${subscriptionExpiryDate}`);
    console.log(`Current Time: ${now}`);
    console.log(`Is Active: ${isActive}`);

    return isActive;
  });
}, [guilds, serverConfigs]);

  // Debugging: Log activeGuilds to verify
  useEffect(() => {
    console.log('Active Guilds:', activeGuilds);
  }, [activeGuilds]);

useEffect(() => {
  if (activeGuilds.length === 1 && selectedGuild !== activeGuilds[0].id) {
    setSelectedGuild(activeGuilds[0].id);
    setGuildId(activeGuilds[0].id);
  }
}, [activeGuilds, selectedGuild, setSelectedGuild, setGuildId]);


useEffect(() => {
  if (selectedGuild) {
    const isSelectedGuildActive = activeGuilds.some(guild => guild.id === selectedGuild);
    if (!isSelectedGuildActive) {
      console.log('ServerSetup: Selected guild is no longer active. Resetting selection.');
      setSelectedGuild('');
      setGuildId('');
      setSnackbar({
        open: true,
        message: 'The selected server no longer has an active subscription. Please select another server.',
        severity: 'warning',
      });
    }
  }
}, [activeGuilds, selectedGuild, setSelectedGuild, setGuildId]);



  const fetchWithRetry = useCallback(
    async (url, headers = {}, retries = 3, delay = 1000) => {
      try {
        const response = await axios.get(url, { headers });
        return response.data;
      } catch (error) {
        if (retries > 0 && error.response && error.response.status === 429) {
          const retryAfter = error.response.headers['retry-after'];
          const waitTime = retryAfter ? parseInt(retryAfter, 10) * 1000 : delay;
          console.warn(`Rate limited. Retrying after ${waitTime / 1000} seconds.`);
          await new Promise((res) => setTimeout(res, waitTime));
          return fetchWithRetry(url, headers, retries - 1, delay * 2);
        } else {
          console.error('Fetch error:', error.response ? error.response.data : error.message);
          return null;
        }
      }
    },
    []
  );



  let MAX_COLLECTIONS_ALLOWED = 1; // Default for 'basic'
  if (subscriptionLevel === 'plus') {
    MAX_COLLECTIONS_ALLOWED = 2;
  } else if (subscriptionLevel === 'premium') {
    MAX_COLLECTIONS_ALLOWED = 5;
  }
  MAX_COLLECTIONS_ALLOWED += additionalCollectionSlots;

  const fetchServerConfig = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/server-config?guildId=${selectedGuild}`);
      const serverConfig = response.data;
  
      setCollectionList(serverConfig.collections || []);
      setRoleConfigurations(serverConfig.roleConfigurations || []);
      setSubscriptionLevel(serverConfig.subscriptionLevel || 'basic');
      setAdditionalCollectionSlots(serverConfig.additionalCollectionSlots || 0);
  
      // Calculate max collections allowed
      const maxCollections = getMaxCollectionsAllowed(serverConfig.subscriptionLevel, serverConfig.additionalCollectionSlots);
      setMaxCollectionsAllowed(maxCollections);
    } catch (error) {
      console.error('Error fetching server configuration:', error);
    }
  };

  const getMaxCollectionsAllowed = (subscriptionLevel, additionalCollectionSlots) => {
    let baseLimit = 1; // Default for 'basic'
  
    if (subscriptionLevel === 'plus') {
      baseLimit = 2;
    } else if (subscriptionLevel === 'premium') {
      baseLimit = 5;
    }
  
    return baseLimit + (additionalCollectionSlots || 0);
  };


  // Update maxCollectionsAllowed when subscription data changes
  useEffect(() => {
    const maxCollections = getMaxCollectionsAllowed(subscriptionLevel, additionalCollectionSlots);
    setMaxCollectionsAllowed(maxCollections);
  }, [subscriptionLevel, additionalCollectionSlots]);

  const handleOpenAddCollectionModal = () => {
    if (collectionList.length >= maxCollectionsAllowed) {
      // User has reached the limit
      setPaymentReason('additional_collection_slot');
      setPaymentAmount(0.5); // Set the amount for purchasing an additional collection slot
      setIsSolanaPaymentModalOpen(true);
    } else {
      // User can add a collection
      setIsAddCollectionModalOpen(true);
    }
  };



const initiatePayment = (reason, additionalData = {}) => {
  setPaymentReason(reason);
  if (reason === 'subscription') {
    setPaymentAmount(1); // Example amount for subscription; adjust as needed
  } else if (reason === 'additional_collection_slot') {
    setPaymentAmount(0.5); // Example amount for additional slot; adjust as needed
  } else if (reason === 'update_collection') {
    setPaymentAmount(0.3); // Example amount for updating a collection; adjust as needed
    setCollectionBeingUnlocked(additionalData.collection); // Pass additional data if needed
  }
  setIsSolanaPaymentModalOpen(true);
};



  /**
   * Fetch Channels, Categories, and Roles for the selected guild.
   * Defined using useCallback to memoize the function and prevent unnecessary re-creations.
   */
  const fetchChannelsAndCategories = useCallback(async () => {
    if (selectedGuild && accessToken) {
      try {
        const headers = {
          Authorization: `Bearer ${accessToken}`,
        };

        // Fetch Channels and Categories
        const channelsResponse = await fetchWithRetry(
          `${process.env.REACT_APP_BACKEND_URL}/api/discord/guilds/${selectedGuild}/channels`,
          headers
        );

        if (
          channelsResponse &&
          Array.isArray(channelsResponse.textChannels) &&
          Array.isArray(channelsResponse.categories)
        ) {
          setChannels(channelsResponse.textChannels);
          setCategories(channelsResponse.categories);
        } else {
          setChannels([]);
          setCategories([]);
        }

        // Fetch Roles
        const rolesData = await fetchWithRetry(
          `${process.env.REACT_APP_BACKEND_URL}/api/discord/guilds/${selectedGuild}/roles`,
          headers
        );

        if (Array.isArray(rolesData)) {
          setRoles(rolesData);
        } else {
          setRoles([]);
        }
      } catch (error) {
        console.error('Error fetching guild data:', error);
        setChannels([]);
        setCategories([]);
        setRoles([]);
        setSnackbar({
          open: true,
          message: 'Failed to fetch guild data. Please try again later.',
          severity: 'error',
        });
      }
    }
  }, [selectedGuild, accessToken, fetchWithRetry]);

  /**
   * Fetch Channels and Categories when a guild is selected.
   */
  useEffect(() => {
    fetchChannelsAndCategories();
  }, [fetchChannelsAndCategories]);

  /**
   * Fetch Collections when a guild is selected.
   */
  useEffect(() => {
    if (selectedGuild && accessToken) {
      const fetchCollections = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/server-config/${selectedGuild}/collections`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );

          if (response.data && response.data.collections) {
            setCollectionList(response.data.collections);
          } else {
            setCollectionList([]);
          }
        } catch (error) {
          console.error('Error fetching collections:', error);
          setCollectionList([]);
          setSnackbar({
            open: true,
            message: 'Failed to fetch collections. Please try again later.',
            severity: 'error',
          });
        }
      };
      fetchCollections();
    } else {
      setCollectionList([]);
    }
  }, [selectedGuild, accessToken]);

  const fetchSubscriptionData = useCallback(async () => {
    if (selectedGuild && accessToken) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/server-config?guildId=${selectedGuild}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
  
        if (response.data) {
          setSubscriptionLevel(response.data.subscriptionLevel || 'basic');
          setAdditionalCollectionSlots(response.data.additionalCollectionSlots || 0);
  
          // Calculate max collections allowed
          const maxCollections = getMaxCollectionsAllowed(
            response.data.subscriptionLevel,
            response.data.additionalCollectionSlots
          );
          setMaxCollectionsAllowed(maxCollections);
        } else {
          setSubscriptionLevel('basic');
          setAdditionalCollectionSlots(0);
          setMaxCollectionsAllowed(1);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
        setSubscriptionLevel('basic');
        setAdditionalCollectionSlots(0);
        setMaxCollectionsAllowed(1);
      }
    }
  }, [selectedGuild, accessToken]);


  // Call fetchSubscriptionData when dependencies change
  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

const [isLoading, setIsLoading] = useState(false);


  /**
   * Function to fetch roles from the backend
   */
  const refreshRoles = useCallback(async () => {
    if (selectedGuild && accessToken) {
      try {
        const headers = {
          Authorization: `Bearer ${accessToken}`,
        };

        const rolesData = await fetchWithRetry(
          `${process.env.REACT_APP_BACKEND_URL}/api/discord/guilds/${selectedGuild}/roles`,
          headers
        );

        if (Array.isArray(rolesData)) {
          setRoles(rolesData);
        } else {
          setRoles([]);
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        setRoles([]);
        setSnackbar({
          open: true,
          message: 'Failed to fetch roles. Please try again later.',
          severity: 'error',
        });
      }
    }
  }, [selectedGuild, accessToken, fetchWithRetry]);

  /**
   * Fetch roles when the selected guild changes
   */
  useEffect(() => {
    refreshRoles();
  }, [refreshRoles]);

  /**
   * Callback function to handle role creation
   */
  const handleRoleCreated = () => {
    refreshRoles(); // Fetch the latest roles
    // Optionally, show a success message or perform other actions
    setSnackbar({
      open: true,
      message: 'Role created successfully!',
      severity: 'success',
    });
  };

  /**
   * Fetch Traits when collectionList changes.
   */
  useEffect(() => {
    if (collectionList.length > 0) {
      const fetchTraits = async () => {
        try {
          const traitPromises = collectionList.map((collection) =>
            axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/nft/traits?collectionAddress=${collection.collectionAddress}`)
          );

          const responses = await Promise.all(traitPromises);

          const allTraits = responses.reduce((acc, response, index) => {
            const collection = collectionList[index];
            if (response.data && response.data.traits) {
              const filteredTraits = response.data.traits.filter(
                (trait) => trait.trait_type !== 'Rarity Rank'
              );

              const mappedTraits = filteredTraits.map((trait) => ({
                ...trait,
                collectionAddress: collection.collectionAddress,
              }));

              return acc.concat(mappedTraits);
            } else {
              console.warn(`No traits found for collection: ${collection.collectionAddress}`);
              return acc;
            }
          }, []);

          setTraits(allTraits);
          console.log('Fetched All Traits:', allTraits); // Debugging
        } catch (error) {
          console.error('Error fetching traits from backend:', error);
          setTraits([]);
          setSnackbar({
            open: true,
            message: 'Failed to fetch traits. Please try again later.',
            severity: 'error',
          });
        }
      };
      fetchTraits();
    } else {
      // If no collections, reset traits
      setTraits([]);
    }
  }, [collectionList]);

/**
 * Function to initiate the save configuration process.
 * Validates input and opens the confirmation modal.
 */
  const handleSaveConfig = () => {
    console.log('Initiating save configuration...');

    // Perform existing validations
    if (!selectedGuild) {
      setSnackbar({
        open: true,
        message: 'Please select a server to configure.',
        severity: 'warning',
      });
      return;
    }

    if (collectionList.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please add at least one NFT Collection.',
        severity: 'warning',
      });
      return;
    }

    if (roleConfigurations.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please provide at least one role configuration.',
        severity: 'warning',
      });
      return;
    }

    // Validate each role configuration
    for (let i = 0; i < roleConfigurations.length; i++) {
      const config = roleConfigurations[i];
      if (!config.roleId) {
        setSnackbar({
          open: true,
          message: `Please select a role for Role Configuration ${i + 1}.`,
          severity: 'warning',
        });
        return;
      }

      if (config.type === 'individual') {
        if (!config.requirements || config.requirements.length === 0) {
          setSnackbar({
            open: true,
            message: `Please add at least one collection requirement for Role Configuration ${i + 1}.`,
            severity: 'warning',
          });
          return;
        }
        for (let j = 0; j < config.requirements.length; j++) {
          const req = config.requirements[j];
          if (!req.collectionAddress) {
            setSnackbar({
              open: true,
              message: `Please select a collection for Role Configuration ${i + 1}, Requirement ${j + 1}.`,
              severity: 'warning',
            });
            return;
          }
          if (!req.count || req.count <= 0) {
            setSnackbar({
              open: true,
              message: `Please enter a valid count for Role Configuration ${i + 1}, Requirement ${j + 1}.`,
              severity: 'warning',
            });
            return;
          }
        }
      } else if (config.type === 'combined') {
        if (
          !config.combinedRequirement ||
          !config.combinedRequirement.collectionAddresses ||
          config.combinedRequirement.collectionAddresses.length === 0
        ) {
          setSnackbar({
            open: true,
            message: `Please select at least one collection for combined requirements in Role Configuration ${i + 1}.`,
            severity: 'warning',
          });
          return;
        }
        if (
          !config.combinedRequirement.combinedCount ||
          config.combinedRequirement.combinedCount <= 0
        ) {
          setSnackbar({
            open: true,
            message: `Please enter a valid combined NFT count for Role Configuration ${i + 1}.`,
            severity: 'warning',
          });
          return;
        }
      } else {
        setSnackbar({
          open: true,
          message: `Invalid type for Role Configuration ${i + 1}.`,
          severity: 'warning',
        });
        return;
      }
    }

    // Prepare configData for the modal
    const configData = {
      selectedGuildName: getSelectedGuildName(), // Implement this function as per your logic
      channelName: channels.find(c => c.id === channel)?.name || 'Unknown Channel',
      roleConfigurations: roleConfigurations, // Pass the entire array as is
      getRoleNameById: getRoleNameById, // Function to get role names
      getCollectionNameByAddress: getCollectionNameByAddress, // Function to get collection names
    };

    setModalConfigData(configData);
    setIsConfirmModalOpen(true);
  };

const addRoleConfiguration = (newConfig) => {
  setRoleConfigurations((prevConfigs) => [...prevConfigs, newConfig]);
};

/**
 * Function to save the configuration after user confirmation.
 */
const saveConfiguration = async () => {
  try {
    setIsSavingConfig(true);
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    };

    const subscriptionExpiresAt = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    const discordUserId = localStorage.getItem('discordUserId');

    // Prepare the configuration data to be saved
    const configData = {
      guildId: selectedGuild,
      channel,
      collectionList: collectionList.map((col) => ({
        collectionAddress: col.collectionAddress,
        name: col.name,
        isLocked: col.isLocked,
        isActive: col.isActive,
        paymentHash: col.paymentHash,
      })),
      subscriptionExpiresAt,
      discordUserId,
      roleConfigurations: roleConfigurations.map((roleConfig) => ({
        roleId: roleConfig.roleId,
        type: roleConfig.type,
        requirements: roleConfig.type === 'individual' ? roleConfig.requirements : [],
        combinedRequirement:
          roleConfig.type === 'combined' ? roleConfig.combinedRequirement : null,
      })),
      subscriptionLevel,
    };

    // Make the API request to save the configuration
    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/server-config`, configData, { headers });

    // **After Saving, Automatically Relock Updated Collections**
    const updatedCollectionList = collectionList.map((col) => ({
      ...col,
      isLocked: true, // Automatically relock after saving
    }));
    setCollectionList(updatedCollectionList);

    // **Reset Relevant States**
    setChannel(''); // Reset channel
    setSelectedCategoryId(''); // Reset selected category
    setNewChannelName(''); // Reset new channel name
    setNewCategoryName(''); // Reset new category name
    setRole(''); // Reset role
    setTraits([]); // Reset traits
    setIsTraitModalOpen(false); // Close trait modal if open
    setSelectedTraitIndex(null); // Reset selected trait index

    // **Show Success Snackbar with Updated Message**
    setSnackbar({
      open: true,
      message: 'Configuration saved successfully and collections relocked! Please check the "Current Configs" page to update/edit your configurations.',
      severity: 'success',
    });

    // **Close the Confirmation Modal**
    setIsConfirmModalOpen(false);

    // **Optional: Redirect to "Current Configs" Page**
    // navigate('/current-configs'); // Uncomment if you want to redirect
  } catch (error) {
    console.error('Error saving configuration:', error);
    const errorMsg = error.response?.data?.error || error.message;
    setSnackbar({
      open: true,
      message: `Failed to save configuration: ${errorMsg}`,
      severity: 'error',
    });
  } finally {
    setIsSavingConfig(false);
  }
};

  const handleEditCollection = (collectionAddress) => {
    console.log('handleEditCollection invoked with:', collectionAddress);
    if (!collectionAddress) {
      console.error('Invalid collectionAddress:', collectionAddress);
      setSnackbar({ open: true, message: 'Invalid collection address.', severity: 'error' });
      return;
    }
    setCurrentCollectionAddress(collectionAddress);
    setIsEditModalOpen(true);
  };

const handleUpdateSuccess = (data) => {
  // Update the local collection list if necessary
  setCollectionList(data.collections);
  // Optionally, display a success message or perform other actions
};

const handleUpdateFailure = (error) => {
  // Handle failure (e.g., logging, additional UI feedback)
  console.error('Update failed:', error);
};



/**
 * Handles adding a new collection.
 * After successfully adding, it refreshes the channels and categories dropdowns.
 */
const handleAddCollection = async () => {
  if (!newCollectionAddress) {
    setSnackbar({
      open: true,
      message: 'Please enter a collection address.',
      severity: 'warning',
    });
    return;
  }

  try {
    // Attempt to add the collection
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/server-config/${selectedGuild}/collections`,
      {
        collectionAddress: newCollectionAddress,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    // Update the collection list
    setCollectionList(response.data.collections);

    // Refresh channels and categories
    fetchChannelsAndCategories();

    // Show success snackbar
    setSnackbar({
      open: true,
      message: 'Collection added successfully!',
      severity: 'success',
    });

    // Close the modal and reset input field
    setIsAddCollectionModalOpen(false);
    setNewCollectionAddress('');
  } catch (error) {
    console.error('Error adding collection:', error);
    const errorMsg = error.response?.data?.error || error.message;

    if (error.response && error.response.status === 403) {
      // Set payment reason and amount
      setPaymentReason('additional_collection_slot');
      setPaymentAmount(0.5); // Ensure amount is set

      // Open the Solana payment modal
      setIsSolanaPaymentModalOpen(true);
      // Close the add collection modal
      setIsAddCollectionModalOpen(false);
    } else {
      setSnackbar({
        open: true,
        message: `Failed to add collection: ${errorMsg}`,
        severity: 'error',
      });
    }
  }
};

  /**
   * Handles removing a collection.
   * After successfully removing, it refreshes the channels and categories dropdowns.
   */
  const handleRemoveCollection = async () => {
    if (!collectionToRemove) {
      setSnackbar({
        open: true,
        message: 'No collection selected for removal.',
        severity: 'warning',
      });
      return;
    }

    try {
      // Call backend API to remove the collection
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/server-config/${selectedGuild}/collections`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          data: { collectionAddress: collectionToRemove.collectionAddress },
        }
      );

      // Assuming the backend returns the updated list
      setCollectionList(response.data.collections);

      // Refresh channels and categories to reflect any changes related to collections
      fetchChannelsAndCategories();

      // Show success snackbar
      setSnackbar({
        open: true,
        message: 'Collection removed successfully!',
        severity: 'success',
      });

      // Close the confirmation modal and reset the collection to remove
      setIsConfirmRemoveModalOpen(false);
      setCollectionToRemove(null);
    } catch (error) {
      console.error('Error removing collection:', error);
      const errorMsg = error.response?.data?.error || error.message;
      setSnackbar({
        open: true,
        message: `Failed to remove collection: ${errorMsg}`,
        severity: 'error',
      });
    }
  };

const handleUnlockCollectionClick = (collection) => {
  setCollectionBeingUnlocked(collection);
  setPaymentReason('unlock_collection');
  setPaymentAmount(0.5); // Set the appropriate amount for unlocking
  setIsSolanaPaymentModalOpen(true);
};

const handleUnlockCollectionSuccess = async (transactionDetails) => {
  try {
    const { signature } = transactionDetails;
    const collection = collectionBeingUnlocked;

    // Ensure collection and collectionAddress exist
    if (!collection || !collection.collectionAddress) {
      throw new Error('Collection information is missing.');
    }

    // Call backend to unlock the collection
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/server-config/${selectedGuild}/collections/unlock`,
      {
        collectionAddress: collection.collectionAddress,
        transactionSignature: signature,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    // Update the collection list locally
    setCollectionList(prevList =>
      prevList.map(col =>
        col.collectionAddress === collection.collectionAddress
          ? { ...col, isLocked: false, transactionSignature: signature, unlockedAt: new Date() }
          : col
      )
    );

    setSnackbar({
      open: true,
      message: 'Collection unlocked successfully!',
      severity: 'success',
    });

    setCollectionBeingUnlocked(null);
  } catch (error) {
    console.error('Error unlocking collection:', error);
    const errorMsg = error.response?.data?.error || error.message;
    setSnackbar({
      open: true,
      message: `Failed to unlock collection: ${errorMsg}`,
      severity: 'error',
    });
  }
};



  /**
   * Callback function to handle Snackbar from modal
   */
  const handleSaveSuccess = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message: message,
      severity: severity,
    });
  };

  /**
   * Function to handle editing a collection (Placeholder for future implementation)
   */
  // Function to prompt user and edit collection address
  const promptAndEditCollection = async (collection) => {
    const newCollectionAddress = prompt("Enter the new collection address:", collection.collectionAddress);
    if (!newCollectionAddress) {
      setSnackbar({
        open: true,
        message: 'Collection address cannot be empty.',
        severity: 'warning',
      });
      return;
    }

    // Basic frontend validation for the new address format (adjust regex as per your requirements)
    const addressRegex = /^0x[a-fA-F0-9]{40}$/; // Example: Ethereum address format
    if (!addressRegex.test(newCollectionAddress)) {
      setSnackbar({
        open: true,
        message: 'Invalid collection address format.',
        severity: 'error',
      });
      return;
    }

    try {
      // Call backend to update the collection address
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/server-config/${selectedGuild}/collections/update-address`,
        {
          currentCollectionAddress: collection.collectionAddress,
          newCollectionAddress: newCollectionAddress,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      // Update the collection list locally
      setCollectionList(prevList =>
        prevList.map(col =>
          col.collectionAddress === collection.collectionAddress
            ? { ...col, collectionAddress: newCollectionAddress }
            : col
        )
      );

      setSnackbar({
        open: true,
        message: 'Collection address updated successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating collection address:', error);
      const errorMsg = error.response?.data?.error || error.message;
      setSnackbar({
        open: true,
        message: `Failed to update collection address: ${errorMsg}`,
        severity: 'error',
      });
    }
  };

const handlePaymentSuccess = (transactionDetails) => {
  if (paymentReason === 'subscription') {
    handleSubscriptionPaymentSuccess(transactionDetails);
  } else if (paymentReason === 'additional_collection_slot') {
    handleAdditionalSlotPaymentSuccess(transactionDetails);
  } else if (paymentReason === 'unlock_collection') {
    handleUnlockCollectionSuccess(transactionDetails); // Correct function
  }

  setIsSolanaPaymentModalOpen(false);
};

const handleSubscriptionPaymentSuccess = async (transactionDetails) => {
  try {
    const { signature, amount, payer, recipient } = transactionDetails;

    // Call backend to update subscription
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/update-subscription`,
      {
        guildId: selectedGuild,
        subscriptionExpiresAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        subscriptionLevel: 'basic', // Adjust based on your subscription levels
        transactionSignature: signature,
        amount,
        payer,
        recipient,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    // Fetch updated subscription data
    await fetchSubscriptionData();
  } catch (error) {
    console.error('Error updating subscription:', error);
    setSnackbar({
      open: true,
      message: `Failed to update subscription: ${error.response?.data?.error || error.message}`,
      severity: 'error',
    });
  }
};

const handleAdditionalSlotPaymentSuccess = useCallback(async (transactionDetails) => {
  if (isPaymentProcessed) return; // Prevent duplicate processing
  
  setIsPaymentProcessed(true); // Lock the state
  try {
    const { signature, amount, payer, recipient } = transactionDetails;
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/server-config/${selectedGuild}/purchase-collection-slot`,
      {
        transactionSignature: signature,
        amount,
        payer,
        recipient,
        slotsToPurchase: 1,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    await fetchSubscriptionData();
  } catch (error) {
    console.error('Error purchasing additional slot:', error);
    setSnackbar({
      open: true,
      message: `Failed to purchase additional slot: ${error.response?.data?.error || error.message}`,
      severity: 'error',
    });
  } finally {
    setIsPaymentProcessed(false); // Unlock after the request is complete
  }
}, [isPaymentProcessed, selectedGuild, accessToken, fetchSubscriptionData]);

const handleUpdateCollectionPaymentSuccess = async (transactionDetails) => {
  try {
    const { signature, amount, payer, recipient } = transactionDetails;
    const { collectionAddress, newName } = collectionBeingUnlocked; // Ensure these are set

    // Call backend to update the collection
    await axios.post(
      '${process.env.REACT_APP_BACKEND_URL}/api/update-collection',
      {
        guildId: selectedGuild,
        collectionAddress,
        newName,
        paymentHash: signature,
        amount,
        payer,
        recipient,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    // Update the collection list
    setCollectionList(prevList =>
      prevList.map(col =>
        col.collectionAddress === collectionAddress
          ? { ...col, name: newName }
          : col
      )
    );
  } catch (error) {
    console.error('Error updating collection:', error);
    setSnackbar({
      open: true,
      message: `Failed to update collection: ${error.response?.data?.error || error.message}`,
      severity: 'error',
    });
  }
};



const handlePaymentFailure = (error) => {
  console.error('Payment failed:', error);
  setSnackbar({
    open: true,
    message: `Payment failed: ${error.message}`,
    severity: 'error',
  });
};

const isSaveDisabled =
  !channel ||
  collectionList.length === 0 ||
  roleConfigurations.length === 0 ||
  roleConfigurations.some((config) => {
    if (!config.roleId) return true; // Role ID is mandatory for all configurations

    if (config.type === 'individual') {
      // For 'individual' type, requirements must be valid
      if (!config.requirements || config.requirements.length === 0) return true;
      return config.requirements.some(
        (req) => !req.collectionAddress || !req.count || req.count <= 0
      );
    } else if (config.type === 'combined') {
      // For 'combined' type, combinedRequirement must be valid
      if (
        !config.combinedRequirement ||
        !config.combinedRequirement.collectionAddresses ||
        config.combinedRequirement.collectionAddresses.length === 0 ||
        !config.combinedRequirement.combinedCount ||
        config.combinedRequirement.combinedCount <= 0
      ) {
        return true;
      }
      return false;
    }

    // If type is neither 'individual' nor 'combined', disable save
    return true;
  });


// Helper function to get the selected guild's name
const getSelectedGuildName = () => {
  const guild = guilds.find(g => g.id === selectedGuild);
  return guild ? guild.name : 'Unknown Server';
};

// Helper function to get role name by ID
const getRoleNameById = (roleId) => {
  const role = roles.find((r) => r.id === roleId);
  if (role) {
    console.log(`Found Role: ${role.name} for Role ID: ${roleId}`);
    return role.name;
  } else {
    console.warn(`Role ID: ${roleId} not found in roles.`);
    return 'Unknown Role';
  }
};

// Helper function to get collection name by address
const getCollectionNameByAddress = (collectionAddress) => {
  const normalizedAddress = collectionAddress.toLowerCase();
  const collection = collectionList.find(
    (col) => col.collectionAddress.toLowerCase() === normalizedAddress
  );
  if (collection) {
    console.log(`Found Collection: ${collection.name} for Address: ${collectionAddress}`);
    return collection.name;
  } else {
    console.warn(`Collection Address: ${collectionAddress} not found in collectionList.`);
    return 'Unknown Collection';
  }
};

  return (
    <Grid
      container
      sx={{
        maxWidth: '900px',
        width: '100%',
        backgroundColor: '#040813',
        backgroundImage: 'linear-gradient(145deg, #040813, #081529)',
        borderRadius: '12px',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.6)',
        padding: 4,
        color: '#E0E4ED',
        margin: 'auto',
        mt: 6,
        mb: 6,
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      <Grid item xs={12}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{
            color: '#9AC0CD',
            fontFamily: 'Poppins, sans-serif',
          }}
        >
          Server Setup
        </Typography>
        {!accessToken ? (
          <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" mt={4}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: '#728C63',
                color: '#FFFFFF',
                borderRadius: '8px',
                padding: '6px 16px',
                textTransform: 'none',
                fontFamily: 'Poppins, sans-serif',
                '&:hover': {
                  backgroundColor: '#8BA675',
                },
              }}
              onClick={() => navigate('/login')}
            >
              Login with Discord
            </Button>
            <Typography variant="body1" sx={{ color: '#A1D1F0', mt: 2 }}>
              Please log in to access server configurations.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Server Selection */}
            <ServerSelector
              guilds={activeGuilds}
              selectedGuild={selectedGuild}
              setSelectedGuild={setSelectedGuild}
              setGuildId={setGuildId}
            />
            {/* Debugging: Log activeGuilds and selectedGuild */}
            {console.log('Rendering ServerSelector with activeGuilds:', activeGuilds)}
            {console.log('Currently selectedGuild:', selectedGuild)}
	    {/* Handle Loading State */}
	    {guilds.length > 0 && serverConfigs.length === 0 && (
	      <Typography variant="body1" sx={{ color: '#A1D1F0', mt: 2 }}>
	        Fetching server configurations...
	      </Typography>
	    )}
	    
	    {/* Handle No Active Guilds */}
	    {guilds.length > 0 && serverConfigs.length > 0 && activeGuilds.length === 0 && (
	      <Typography variant="body1" sx={{ color: '#A1D1F0', mt: 2 }}>
	        You do not have any guilds with active subscriptions. Please subscribe to a server to proceed.
	      </Typography>
	    )}

            {selectedGuild && activeGuilds.length > 0 && (
              <Box sx={{ mt: 4 }}>
                {/* Collection Management Section */}
                <Typography variant="h6" sx={{ color: '#A1D1F0', mb: 2 }}>
                  Manage NFT Collections
                </Typography>
                <Button
                  variant="outlined"
                  sx={{
                    color: '#A1D1F0',
                    borderColor: '#A1D1F0',
                    textTransform: 'none',
                    fontFamily: 'Poppins, sans-serif',
                    '&:hover': {
                      borderColor: '#9AC0CD',
                      backgroundColor: 'rgba(154, 192, 205, 0.1)',
                    },
                  }}
                  onClick={() => setIsAddCollectionModalOpen(true)}
                  startIcon={<AddIcon />}
                >
                  Add Collection
                </Button>

                {/* List of Collections */}
                <Box sx={{ mt: 2 }}>
                  {collectionList.length > 0 ? (
                    <CustomScrollbarBox>
                      {collectionList.map((collection) => (
                        <Box
                          key={collection.collectionAddress}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: '#132A47',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            opacity: collection.isLocked ? 0.7 : 1,
                            transition: 'opacity 0.3s',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {/* Lock Icon */}
                            {collection.isLocked ? (
                              <Tooltip title="This collection is locked. Click to unlock">
                                <LockIcon 
				   sx={{ color: '#FF6B6B', cursor: 'pointer' }}
				   onClick={() => handleUnlockCollectionClick(collection)} 
				 />
                              </Tooltip>
                            ) : (
                              <Tooltip title="This collection is unlocked. It will auto-lock after changes are made.">
                                <LockOpenIcon sx={{ color: '#A1D1F0' }} />
                              </Tooltip>
                            )}

                            <Box>
                              <Typography sx={{ color: '#E0E4ED', fontWeight: 'bold' }}>
                                {collection.name || 'Unnamed Collection'}
                              </Typography>
                              <Typography sx={{ color: '#A1D1F0', fontSize: '0.9em' }}>
                                {collection.collectionAddress}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {/* Edit Collection Button */}
                            <Tooltip
                              title={
                                collection.isLocked
                                  ? 'Cannot modify a locked collection.'
                                  : 'Edit Collection'
                              }
                            >
                              <span>
                                <IconButton
                                  onClick={() => handleEditCollection(collection.collectionAddress)}
                                  disabled={collection.isLocked}
                                  sx={{
                                    color: collection.isLocked ? '#A1D1F0' : '#4C5C64',
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {/* Remove Button */}
                            <Tooltip
                              title={
                                collection.isLocked
                                  ? 'This collection is locked. Unlock to remove.'
                                  : 'Remove Collection'
                              }
                            >
                              <span>
                                <IconButton
                                  onClick={() => {
                                    setCollectionToRemove(collection); // Set the collection to remove
                                    setIsConfirmRemoveModalOpen(true); // Open the confirmation dialog
                                  }}
                                  disabled={collection.isLocked}
                                  sx={{
                                    color: collection.isLocked ? '#A1D1F0' : '#FF6B6B',
                                  }}
                                >
                                  <RemoveIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </CustomScrollbarBox>
                  ) : (
                    <Typography sx={{ color: '#E0E4ED' }}>No collections added yet.</Typography>
                  )}
                </Box>

                {/* Add Collection Modal */}
                <Dialog
                  open={isAddCollectionModalOpen}
                  onClose={() => setIsAddCollectionModalOpen(false)}
                >
                  <DialogTitle
                    sx={{
                      color: '#E0E4ED',
                      backgroundColor: '#040813',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                    Add NFT Collection
                  </DialogTitle>
                  <DialogContent
                    sx={{
                      backgroundColor: '#0D1F3A',
                      padding: 3,
                    }}
                  >
                    <TextField
                      label="Collection Address"
                      value={newCollectionAddress}
                      onChange={(e) => setNewCollectionAddress(e.target.value)}
                      placeholder="Enter NFT Collection Address"
                      fullWidth
                      InputProps={{
                        style: {
                          color: '#E0E4ED',
                          backgroundColor: '#132A47',
                          borderRadius: '8px',
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          color: '#D1E5F0',
                        },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#607080',
                          },
                          '&:hover fieldset': {
                            borderColor: '#9AC0CD',
                          },
                        },
                      }}
                    />
                  </DialogContent>
                  <DialogActions
                    sx={{
                      backgroundColor: '#040813',
                      padding: 2,
                    }}
                  >
                    <Button
                      onClick={handleAddCollection}
                      sx={{
                        color: '#A1D1F0',
                        borderColor: '#4C5C64',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#9AC0CD',
                          backgroundColor: 'rgba(154, 192, 205, 0.1)',
                        },
                      }}
                      variant="outlined"
                      startIcon={<AddIcon />}
                    >
                      Add
                    </Button>
                    <Button
                      onClick={() => setIsAddCollectionModalOpen(false)}
                      sx={{
                        color: '#A1D1F0',
                        borderColor: '#4C5C64',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#9AC0CD',
                          backgroundColor: 'rgba(154, 192, 205, 0.1)',
                        },
                      }}
                      variant="outlined"
                      startIcon={<RemoveIcon />}
                    >
                      Cancel
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Confirmation Dialog for Removing Collection */}
                <Dialog
                  open={isConfirmRemoveModalOpen}
                  onClose={() => setIsConfirmRemoveModalOpen(false)}
                >
                  <DialogTitle>Confirm Collection Removal</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      Are you sure you want to remove the collection "
                      {collectionToRemove?.name || 'Unnamed Collection'}"? This action cannot be undone.
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={() => setIsConfirmRemoveModalOpen(false)}
                      variant="outlined"
                      sx={{
                        color: '#A1D1F0',
                        borderColor: '#4C5C64',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#9AC0CD',
                          backgroundColor: 'rgba(154, 192, 205, 0.1)',
                        },
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleRemoveCollection}
                      variant="outlined"
                      sx={{
                        color: '#FF6B6B',
                        borderColor: '#FF6B6B',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#FF6B6B',
                          backgroundColor: 'rgba(255, 107, 107, 0.1)',
                        },
                      }}
                      startIcon={<RemoveIcon />}
                    >
                      Remove
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Channel Configuration */}
                <ChannelConfiguration
                  channels={channels}
                  categories={categories}
                  selectedGuild={selectedGuild}
                  accessToken={accessToken}
                  refreshChannelsAndCategories={fetchChannelsAndCategories} // Pass the refresh function
                  channel={channel}
                  setChannel={setChannel}
                  newChannelName={newChannelName}
                  setNewChannelName={setNewChannelName}
                  selectedCategoryId={selectedCategoryId}
                  setSelectedCategoryId={setSelectedCategoryId}
                  newCategoryName={newCategoryName}
                  setNewCategoryName={setNewCategoryName}
                />

                {/* NFT Configuration */}
                <NFTConfiguration
                  collectionList={collectionList} // Pass the list of collections
                  roleConfigurations={roleConfigurations}
                  setRoleConfigurations={setRoleConfigurations}
                  traits={traits}
                  setTraits={setTraits}
                  isTraitModalOpen={isTraitModalOpen}
                  setIsTraitModalOpen={setIsTraitModalOpen}
                  selectedTraitIndex={selectedTraitIndex}
                  setSelectedTraitIndex={setSelectedTraitIndex}
                  roles={roles} // Pass roles to NFTConfiguration
		  refreshRoles={refreshRoles}
                />

                {/* Edit Collection Modal */}
                <EditCollectionModal
                  open={isEditModalOpen}
                  onClose={() => setIsEditModalOpen(false)}
                  guildId={selectedGuild} // Ensure this is the guildId
                  currentCollectionAddress={currentCollectionAddress}
                  onUpdateSuccess={(data) => {
                    console.log('Update Success:', data);
                    // Optionally, refresh the collection list or update state
                    setCollectionList(data.collections);
                  }}
                  onUpdateFailure={(error) => {
                    console.error('Update Failure:', error);
                  }}
                />

                {/* Purchase Slot Modal */}
                <PurchaseSlotModal
                  open={isPurchaseSlotModalOpen}
                  onClose={() => setIsPurchaseSlotModalOpen(false)}
                  accessToken={accessToken}
                  selectedGuild={selectedGuild}
                  fetchSubscriptionData={fetchSubscriptionData}
                  setSnackbar={setSnackbar}
                  maxCollectionsAllowed={MAX_COLLECTIONS_ALLOWED}
                />

                 <SolanaPaymentModal
                   open={isSolanaPaymentModalOpen}
                   handleClose={() => setIsSolanaPaymentModalOpen(false)}
                   amount={paymentAmount}
                   recipient={'H6BKQchYaFZjBt2i6L5TriKMuTwsQK6UJc5xU5Fy5jJW'} // Replace with your wallet address
                   onPaymentSuccess={handlePaymentSuccess}
                   onPaymentFailure={handlePaymentFailure}
		   selectedGuild={selectedGuild}
		   paymentReason={paymentReason}
		   maxCollectionsAllowed={maxCollectionsAllowed}
		   collectionBeingUnlocked={collectionBeingUnlocked}
                 />

                   <ConfirmConfigurationModal
                     open={isConfirmModalOpen}
                     onClose={() => setIsConfirmModalOpen(false)}
                     configData={{
                       selectedGuildName: getSelectedGuildName(),
                       channelName: channels.find(c => c.id === channel)?.name || 'Unknown Channel',
                       roleConfigurations: roleConfigurations.map((config) => ({
                         ...config,
                         traits: config.requirements.flatMap((req) => req.traits || []), // Aggregate traits from all requirements
                       })),
                       getRoleNameById: getRoleNameById,
                       getCollectionNameByAddress: getCollectionNameByAddress,
                     }}
                     onConfirm={saveConfiguration}
                   />

                {/* Save Configuration Button */}
                <Box sx={{ position: 'relative' }}>
                 <Button
                   variant="contained"
                   sx={{
                     backgroundColor: '#728C63',
                     color: '#FFFFFF',
                     mt: 4,
                     fontFamily: 'Poppins, sans-serif',
                     borderRadius: '8px',
                     padding: '12px 24px',
                     textTransform: 'none',
                     '&:hover': {
                       backgroundColor: '#8BA675',
                     },
                   }}
                   onClick={handleSaveConfig}
                   fullWidth
                   disabled={isSaveDisabled}
                 >
                   Save Configuration
                 </Button>
                  {isSavingConfig && (
                    <Backdrop
                      sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                      open={isSavingConfig}
                    >
                      <CircularProgress color="inherit" />
                    </Backdrop>
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
        {/* Snackbar for Notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
	  action={snackbar.action}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            elevation={6}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Grid>
    </Grid>
  );
};

export default ServerSetup;




